import React, { useState, useEffect, useRef } from 'react';
import { adminAPI, clientsAPI } from '../../services/api';
import Swal from 'sweetalert2';
import {
  BarChart3,
  TrendingUp,
  Users,
  FileText,
  Download,
  Filter,
  RefreshCw,
  Calendar,
  Search,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Clock,
  Package,
  DollarSign,
  Activity,
  Zap,
  X // ← Agregado: Importación del icono X
} from 'lucide-react';

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [error, setError] = useState('');
  const [clientsError, setClientsError] = useState(''); // ← Agregado: Estado faltante
  const [success, setSuccess] = useState('');
  const [exporting, setExporting] = useState(false);

  // Estados para estadísticas
  const [stats, setStats] = useState({
    totalReports: 0,
    totalUsers: 0,
    totalDownloads: 0,
    totalInvoices: 0,
    systemUsage: 0,
    activeClients: 0
  });

  // Estados para filtros y paginación
  const [filters, setFilters] = useState({
    clientId: '',
    reportType: '',
    fechaDesde: '',
    fechaHasta: ''
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false
  });

  const [sorting, setSorting] = useState({
    orderBy: 'CreatedAt',
    orderDirection: 'desc'
  });

  const [showFilters, setShowFilters] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Estado y ref para el efecto de scroll
  const [showStatsInfo, setShowStatsInfo] = useState(true);
  const lastScrollY = useRef(0);
  const scrollTimeout = useRef(null);
  const isScrolling = useRef(false);
  const scrollThreshold = 100;

  useEffect(() => {
    loadInitialData();
  }, []);

  // Cargar reportes cuando cambian filtros, paginación o sorting
  useEffect(() => {
    if (!loading) {
      loadReports();
    }
  }, [filters, pagination.currentPage, sorting]);

  // Auto-refresh cada 2 minutos
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading) {
        console.log('🔄 Actualizando reportes automáticamente...');
        loadReports(true);
        loadStats();
      }
    }, 120000);
    return () => clearInterval(interval);
  }, [filters, pagination.currentPage, sorting]);

  // Efecto de scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (!isScrolling.current) {
        isScrolling.current = true;
        
        if (Math.abs(currentScrollY - lastScrollY.current) > scrollThreshold) {
          setShowStatsInfo(currentScrollY < lastScrollY.current || currentScrollY < 200);
          lastScrollY.current = currentScrollY;
        }
        
        clearTimeout(scrollTimeout.current);
        scrollTimeout.current = setTimeout(() => {
          isScrolling.current = false;
        }, 150);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout.current);
    };
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadClients(),
        loadStats(),
        loadReports()
      ]);
    } catch (error) {
      console.error('Error cargando datos iniciales:', error);
      setError('Error al cargar los datos iniciales');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      setClientsLoading(true);
      const response = await clientsAPI.getAll();
      setClients(response.data?.items || []);
      setClientsError(''); // ← Corregido: Ahora funciona correctamente
    } catch (error) {
      console.error('Error cargando clientes:', error);
      setClientsError('Error al cargar clientes'); // ← Corregido: Ahora funciona correctamente
    } finally {
      setClientsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const [usageResponse, systemResponse] = await Promise.all([
        adminAPI.getUsageReport({ period: '30d' }),
        adminAPI.getSystemStatus()
      ]);
      
      setStats({
        totalReports: usageResponse.data?.totalReports || 0,
        totalUsers: systemResponse.data?.totalUsers || 0,
        totalDownloads: usageResponse.data?.totalDownloads || 0,
        totalInvoices: usageResponse.data?.totalInvoices || 0,
        systemUsage: systemResponse.data?.cpuUsage || 0,
        activeClients: systemResponse.data?.activeClients || 0
      });
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const loadReports = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      
      const params = {
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection,
        ...filters
      };

      const response = await adminAPI.getUsageReport(params);
      
      if (response.data) {
        setReports(response.data.items || []);
        setPagination(prev => ({
          ...prev,
          totalItems: response.data.totalItems || 0,
          totalPages: response.data.totalPages || 0,
          hasPreviousPage: response.data.hasPreviousPage || false,
          hasNextPage: response.data.hasNextPage || false
        }));
        setError('');
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error cargando reportes:', error);
      setError('Error al cargar los reportes');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      clientId: '',
      reportType: '',
      fechaDesde: '',
      fechaHasta: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      orderBy: field,
      orderDirection: prev.orderBy === field && prev.orderDirection === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      
      const params = {
        ...filters,
        format: 'excel',
        includeStats: true
      };

      // Simular llamada a API de exportación
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSuccess('Reporte exportado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error exportando reporte:', error);
      setError('Error al exportar el reporte');
      setTimeout(() => setError(''), 3000);
    } finally {
      setExporting(false);
    }
  };

  const getReportTypeText = (type) => {
    const types = {
      'usage': 'Uso del Sistema',
      'performance': 'Rendimiento',
      'downloads': 'Descargas',
      'invoices': 'Facturas',
      'clients': 'Clientes',
      'errors': 'Errores'
    };
    return types[type] || type;
  };

  const getReportTypeIcon = (type) => {
    const icons = {
      'usage': Activity,
      'performance': Zap,
      'downloads': Download,
      'invoices': FileText,
      'clients': Users,
      'errors': AlertTriangle
    };
    const IconComponent = icons[type] || BarChart3;
    return <IconComponent className="w-4 h-4" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.razonSocial} (${client.rfc})` : 'Cliente no encontrado';
  };

  const getSortIcon = (field) => {
    if (sorting.orderBy !== field) {
      return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    }
    return sorting.orderDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-600" />
      : <ArrowDown className="w-4 h-4 text-blue-600" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Cargando reportes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
                📈 Reportes Administrativos
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Análisis y estadísticas del sistema
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => loadReports()}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
              <button
                onClick={handleExportExcel}
                disabled={exporting}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {exporting ? 'Exportando...' : 'Exportar Excel'}
              </button>
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className={`transition-all duration-500 ease-in-out mb-6 ${
          showStatsInfo ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-4 pointer-events-none'
        }`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <div className="card bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Total Reportes</p>
                  <p className="text-2xl font-bold">{stats.totalReports.toLocaleString()}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-blue-200" />
              </div>
            </div>

            <div className="card bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">Usuarios Activos</p>
                  <p className="text-2xl font-bold">{stats.totalUsers.toLocaleString()}</p>
                </div>
                <Users className="w-8 h-8 text-green-200" />
              </div>
            </div>

            <div className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">Total Descargas</p>
                  <p className="text-2xl font-bold">{stats.totalDownloads.toLocaleString()}</p>
                </div>
                <Download className="w-8 h-8 text-purple-200" />
              </div>
            </div>

            <div className="card bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm font-medium">Facturas Procesadas</p>
                  <p className="text-2xl font-bold">{stats.totalInvoices.toLocaleString()}</p>
                </div>
                <FileText className="w-8 h-8 text-orange-200" />
              </div>
            </div>

            <div className="card bg-gradient-to-br from-red-500 to-red-600 text-white border-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm font-medium">Uso del Sistema</p>
                  <p className="text-2xl font-bold">{stats.systemUsage}%</p>
                </div>
                <Activity className="w-8 h-8 text-red-200" />
              </div>
            </div>

            <div className="card bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-teal-100 text-sm font-medium">Clientes Activos</p>
                  <p className="text-2xl font-bold">{stats.activeClients.toLocaleString()}</p>
                </div>
                <Package className="w-8 h-8 text-teal-200" />
              </div>
            </div>
          </div>
        </div>

        {/* Panel de Filtros */}
        <div className="card mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 border-2 border-blue-200 dark:border-gray-600">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filtros de Búsqueda</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-1 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {showFilters ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showFilters ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cliente
                </label>
                <select
                  value={filters.clientId}
                  onChange={(e) => handleFilterChange('clientId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  disabled={clientsLoading}
                >
                  <option value="">Todos los clientes</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.razonSocial} ({client.rfc})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tipo de Reporte
                </label>
                <select
                  value={filters.reportType}
                  onChange={(e) => handleFilterChange('reportType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="">Todos los tipos</option>
                  <option value="usage">Uso del Sistema</option>
                  <option value="performance">Rendimiento</option>
                  <option value="downloads">Descargas</option>
                  <option value="invoices">Facturas</option>
                  <option value="clients">Clientes</option>
                  <option value="errors">Errores</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha Desde
                </label>
                <input
                  type="date"
                  value={filters.fechaDesde}
                  onChange={(e) => handleFilterChange('fechaDesde', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha Hasta
                </label>
                <input
                  type="date"
                  value={filters.fechaHasta}
                  onChange={(e) => handleFilterChange('fechaHasta', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <X className="w-4 h-4" /> {/* ← Corregido: Ahora el icono X está disponible */}
              Limpiar Filtros
            </button>
            <button
              onClick={() => loadReports()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Search className="w-4 h-4" />
              Actualizar
            </button>
          </div>
        </div>

        {/* Alertas */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <span className="text-red-700 dark:text-red-300">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <span className="text-green-700 dark:text-green-300">{success}</span>
          </div>
        )}

        {/* Tabla de Reportes */}
        <div className="card overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Reportes Generados</h3>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Última actualización: {formatDate(lastUpdate.toISOString())}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('reportType')}
                      className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      Tipo
                      {getSortIcon('reportType')}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('clientId')}
                      className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      Cliente
                      {getSortIcon('clientId')}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('createdAt')}
                      className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      Fecha Generación
                      {getSortIcon('createdAt')}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Período
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Registros
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {reports.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <BarChart3 className="w-12 h-12 text-gray-400" />
                        <p className="text-gray-500 dark:text-gray-400">No se encontraron reportes</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500">
                          Los reportes aparecerán aquí cuando se generen
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  reports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getReportTypeIcon(report.type)}
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {getReportTypeText(report.type)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {report.clientId ? getClientName(report.clientId) : 'Sistema General'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatDate(report.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {report.startDate && report.endDate ? (
                            `${formatDate(report.startDate)} - ${formatDate(report.endDate)}`
                          ) : (
                            'N/A'
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {(report.recordCount || 0).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          report.status === 'completed' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            : report.status === 'processing'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                        }`}>
                          {report.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                          {report.status === 'processing' && <Clock className="w-3 h-3 mr-1" />}
                          {report.status === 'failed' && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {report.status === 'completed' ? 'Completado' : 
                           report.status === 'processing' ? 'Procesando' : 'Error'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          {report.status === 'completed' && (
                            <button
                              onClick={() => window.open(report.downloadUrl, '_blank')}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                              title="Descargar reporte"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => console.log('Ver detalles:', report.id)}
                            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                            title="Ver detalles"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Mostrando {((pagination.currentPage - 1) * pagination.pageSize) + 1} a{' '}
                  {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)} de{' '}
                  {pagination.totalItems} reportes
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPreviousPage}
                    className="px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Anterior
                  </button>
                  <span className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                    Página {pagination.currentPage} de {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    className="px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;